import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { useAuth } from "../../auth";
import ExposureWarningDialog from "../../components/ExposureWarningDialog";
import {
  environmentDBTiers,
  nodepoolOptions,
  resourceAllocationOptions,
  storageClassOptions,
} from "../../global/constants";
import EnvironmentService, { IBackupSchedulePreset } from "../../services/environment.service";
import IstioService, { exposureErrorDetail } from "../../services/istio.service";
import RestoreService from "../../services/restore.service";
import TenantService from "../../services/tenant.service";
import { IPreflight, validateExposureName } from "../../types/api/istio.type";
import { ITenant } from "../../types/api/tenant.type";
import { ICloneSources, IForeignSources, IRestoreWindow } from "../../types/api/restore.type";
import {
  accountsBody,
  backupGroups,
  belowFloor,
  cloneBody,
  CloneForm,
  cloneOutcome,
  exposureNameFor,
  floorOf,
  foreignCloneBody,
  foreignEnvId,
  formFromSpec,
  formFromWindow,
  isForeign,
  latestStream,
  LATEST,
  localLabel,
  refusalMessage,
  Source,
  sourceKey,
  toBytes,
  utcLabel,
  windowBounds,
  windowIsFor,
  withinWindow,
} from "./clone.logic";

const INPUT_FORMAT = "YYYY-MM-DDTHH:mm:ss";

const sourceLabel = (s: Source) =>
  isForeign(s)
    ? `${s.cluster} ${s.env_id} · ${s.account}${s.archived ? " · archived" : ""}`
    : `${s.name || "(no name)"} · ${s.env_id}${s.deleted ? " · deleted" : ""}${s.archived ? " · archived" : ""}`;

const sourceName = (s: Source) => (isForeign(s) ? `${s.cluster} ${s.env_id}` : s.name || String(s.env_id));

const readWindow = (s: Source) =>
  isForeign(s) ? RestoreService.foreignWindow(s.cluster, s.account, s.env_id) : RestoreService.restoreWindow(s.env_id);

/** D9: a clone of one of a tenant's environments, live or deleted, at a point in its window. */
export const EnvironmentClone = ({ onOpenRestore }: { onOpenRestore?: () => void }) => {
  const { user } = useAuth();
  const actor: string | null = user?.email || user?.name || null;
  const owner: number | undefined = (user as any)?.["https://controlrooms.ai/user"]?.user_id;

  const [tenantQuery, setTenantQuery] = useState("");
  const [tenants, setTenants] = useState<ITenant[]>([]);
  const [tenant, setTenant] = useState<ITenant | null>(null);

  // "" is this cluster; otherwise one whose backups can be cloned here (D18).
  const [cluster, setCluster] = useState("");
  const [clusters, setClusters] = useState<string[]>([]);

  const [sourceQuery, setSourceQuery] = useState("");
  const [sources, setSources] = useState<ICloneSources | IForeignSources | null>(null);
  const [source, setSource] = useState<Source | null>(null);

  const [win, setWin] = useState<IRestoreWindow | null>(null);
  const [point, setPoint] = useState<string>("");
  // D16: Latest is its own choice, not the timestamp it shows: it takes everything written when the clone starts.
  const [latest, setLatest] = useState(true);
  const [form, setForm] = useState<CloneForm>(formFromSpec(null));

  const [accountOptions, setAccountOptions] = useState<{ id: string; label: string }[]>([]);
  const [presets, setPresets] = useState<{ full: IBackupSchedulePreset[]; incremental: IBackupSchedulePreset[] }>({
    full: [],
    incremental: [],
  });

  const [sourcesPending, setSourcesPending] = useState(false);
  const [windowPending, setWindowPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ severity: "success" | "warning"; text: string } | null>(null);
  const [confirm, setConfirm] = useState(false);
  // Set when exposing was asked for and preflight refused; ExposureWarningDialog decides what is offered.
  const [noPort, setNoPort] = useState<IPreflight | null>(null);
  // State updates land a render late; the ref is what stops a second click.
  const busyRef = useRef(false);

  useEffect(() => {
    EnvironmentService.getBackupStorageAccountOptions()
      .then(({ data }) => setAccountOptions(data))
      .catch(() => setAccountOptions([]));
    EnvironmentService.getBackupSchedulePresets()
      .then(({ data }) => setPresets(data))
      .catch(() => setPresets({ full: [], incremental: [] }));
    // An older database-manager has no such route: then only this cluster is offered.
    RestoreService.foreignClusters()
      .then(({ data }) => setClusters(data.clusters ?? []))
      .catch(() => setClusters([]));
  }, []);

  // One stream per fetch: only the latest selection's answer counts, and each stream owns its own pending flag.
  const tenantsStream = useMemo(
    () =>
      latestStream(
        (query: string) =>
          TenantService.getTenants({ count_per_page: 20, page_no: 1, filter: { tenant_name: query } }),
        {
          data: ({ data }: any) => setTenants(data?.results ?? []),
          error: (e) => setError(exposureErrorDetail(e).message || "Could not list tenants"),
          pending: () => undefined,
        },
        300
      ),
    []
  );
  const sourcesStream = useMemo(
    () =>
      latestStream(
        ({ cluster, tenantId, q }: { cluster: string; tenantId?: number; q?: string }) =>
          cluster
            ? RestoreService.foreignSources(cluster, foreignEnvId(q ?? ""))
            : RestoreService.cloneSources(tenantId!, q),
        {
          data: ({ data }: { data: ICloneSources | IForeignSources }) => setSources(data),
          error: (e) => setError(exposureErrorDetail(e).message || "Could not list clone sources"),
          pending: setSourcesPending,
        },
        300
      ),
    []
  );
  const windowStream = useMemo(
    () =>
      latestStream(readWindow, {
        data: ({ data }) => {
          // Window, point and spec from one response, so they always describe the same source.
          setWin(data);
          setPoint(data.latest);
          setLatest(true);
          setForm((prev) => ({ ...formFromWindow(data), name: prev.name, password: prev.password }));
        },
        error: (e) => setError(exposureErrorDetail(e).message || "Could not read the source's window"),
        pending: setWindowPending,
      }),
    []
  );

  useEffect(() => tenantsStream(tenantQuery), [tenantQuery, tenantsStream]);

  // Another cluster's sources do not depend on the tenant, so picking one keeps the source (D20).
  const sourceTenantId = cluster ? undefined : tenant?.tenant_id;
  useEffect(() => {
    setSource(null);
    setSources(null);
    const q = sourceQuery.trim() || undefined;
    sourcesStream(cluster || sourceTenantId ? { cluster, tenantId: sourceTenantId, q } : null);
  }, [cluster, sourceTenantId, sourceQuery, sourcesStream]);

  useEffect(() => {
    setWin(null);
    setPoint("");
    windowStream(source);
  }, [source, windowStream]);

  // Nothing may report back once the tab is closed.
  useEffect(
    () => () => {
      sourcesStream(null);
      windowStream(null);
    },
    [sourcesStream, windowStream]
  );

  const floor = floorOf(win);
  const set = (field: keyof CloneForm) => (e: any) =>
    setForm((prev) => ({ ...prev, [field]: e?.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const problems = useMemo(() => {
    const out: Partial<Record<"name" | "storage" | "point" | "cpu_req" | "memory_req" | "exposure_name", string>> = {};
    if (!form.name.trim()) out.name = "Required";
    if (toBytes(form.storage) === null) out.storage = "A size such as 100Gi";
    else if (belowFloor(form.storage, floor)) out.storage = `At least ${floor}, the source's size`;
    if (!form.cpu_req) out.cpu_req = "Required";
    if (!form.memory_req) out.memory_req = "Required";
    if (!win || (!latest && (!point || !withinWindow(point, win)))) out.point = "Pick a time inside the window";
    if (form.expose_externally) {
      const invalid = validateExposureName(exposureNameFor(form, tenant?.tenant_name));
      if (invalid) out.exposure_name = invalid;
    }
    return out;
  }, [form, floor, win, point, latest, tenant]);

  const ready =
    !!tenant?.tenant_id && !!source && windowIsFor(win, source) && !win?.archived && !!actor && owner !== undefined &&
    sources?.enabled !== false && Object.keys(problems).length === 0;

  const submit = useCallback(async (proceedWithoutExposure = false) => {
    if (busyRef.current || !tenant?.tenant_id || !source || !windowIsFor(win, source) || !actor || owner === undefined) {
      return;
    }
    busyRef.current = true;
    setSubmitting(true);
    setConfirm(false);
    setError(null);
    setOutcome(null);
    try {
      // Re-read, not stored: a live source's window has moved on, and retention may have moved its start.
      const { data: fresh } = await readWindow(source);
      if (!latest && !withinWindow(point, fresh)) {
        setError(`The point is no longer inside the window: ${utcLabel(fresh.earliest)} to ${utcLabel(fresh.latest)}.`);
        return;
      }
      const exposureName = form.expose_externally ? exposureNameFor(form, tenant.tenant_name) : undefined;
      if (form.expose_externally && !proceedWithoutExposure) {
        // Before anything is created, as the create form does: a port may not be free.
        let check: IPreflight;
        try {
          check = (await IstioService.preflight(undefined, exposureName)).data;
        } catch {
          check = { can_expose: false, reason: "capacity_unknown", free_count: 0, fresh_count: 0, declared_count: 0, low_watermark: false };
        }
        if (!check.can_expose) {
          setNoPort(check);
          return;
        }
      }
      let target: { env_id: number; env_uuid: string };
      try {
        const created = await EnvironmentService.createEnvironment(accountsBody(form, tenant.tenant_id, owner));
        target = { env_id: created.data.env_id, env_uuid: created.data.env_uuid };
      } catch (e) {
        setError(exposureErrorDetail(e).message || "Could not create the accounts environment");
        return;
      }
      let status: number | undefined;
      let body: any;
      try {
        const request = cloneBody(form, target, latest ? LATEST : point, exposureName, proceedWithoutExposure);
        ({ status, data: body } = await (isForeign(source)
          ? RestoreService.cloneForeign(actor, tenant.tenant_id, foreignCloneBody(source, request))
          : RestoreService.cloneEnvironment(actor, tenant.tenant_id, source.env_id, request)));
      } catch (e) {
        // No answer at all: a timeout or a dropped connection may still have created it.
        body = exposureErrorDetail(e);
      }
      const verdict = cloneOutcome(status);
      if (verdict === "created") {
        setOutcome({
          severity: body.exposure_warning ? "warning" : "success",
          text:
            `Clone ${form.name.trim()} (${body.env_id}) of ${sourceName(source)} at ` +
            `${utcLabel(body.point)} is restoring. Follow it in the Restore tab, and Verify once it is Running.` +
            (body.exposure ? ` Exposed as ${body.exposure.name} on port ${body.exposure.port}.` : "") +
            (body.exposure_warning ? ` It was not exposed: ${body.exposure_warning.message}` : "") +
            (body.archived_before_clone === false
              ? " The source's last write could not be confirmed archived, so the clone may stop short of it."
              : ""),
        });
        setForm((prev) => ({ ...prev, name: "", password: "", exposure_name: "" }));
      } else if (verdict === "refused") {
        // D11: nothing was created, so the accounts entry made for it goes too.
        const detail = exposureErrorDetail(body);
        const removed = await EnvironmentService.deleteEnvironment(target.env_uuid).then(() => true, () => false);
        setError(
          refusalMessage(status, detail) +
            (removed
              ? ` The accounts entry made for it (${target.env_id}) was removed.`
              : ` The accounts entry made for it (${target.env_id}) could not be removed; delete it from the tenant.`)
        );
      } else {
        const said = exposureErrorDetail(body).message || (status ? `HTTP ${status}` : "no answer");
        setOutcome({
          severity: "warning",
          text:
            `The clone request did not complete (${said}), so the database may or may not exist. The accounts ` +
            `entry ${target.env_id} was kept: check environment ${target.env_id} in the Restore tab before trying again.`,
        });
      }
    } catch (e) {
      setError(exposureErrorDetail(e).message || "Could not re-read the source's window");
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  }, [tenant, source, win, actor, owner, point, latest, form]);

  // The server lists the default ("") itself; added only if it ever stops doing so.
  const accounts = accountOptions.some((o) => o.id === "")
    ? accountOptions
    : [{ id: "", label: "Default" }, ...accountOptions];
  const bounds = win ? windowBounds(win) : null;
  const groups = win ? backupGroups(win) : [];
  const inputMin = bounds
    ? bounds.earliest.clone().add(bounds.earliest.milliseconds() ? 1 : 0, "second").format(INPUT_FORMAT)
    : undefined;
  const inputMax = bounds ? bounds.latest.format(INPUT_FORMAT) : undefined;

  return (
    <Box p={2}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {outcome && (
        <Alert
          severity={outcome.severity}
          sx={{ mb: 2 }}
          onClose={() => setOutcome(null)}
          action={onOpenRestore && <Button onClick={onOpenRestore}>Restore tab</Button>}
        >
          {outcome.text}
        </Alert>
      )}
      {sources?.enabled === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Cloning is switched off on this server.</strong> Sources and windows can be browsed;
          a clone is refused until <code>ENVIRONMENT_CLONE_ENABLED</code> is set.
        </Alert>
      )}
      {(!actor || owner === undefined) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Cloning is disabled: the server records who asked for it, and this session has no signed-in identity.
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        1. Source
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {cluster
          ? `A clone of a ${cluster} environment goes into any tenant here, under a new id. ${cluster} is never ` +
            "written to: its backups are read with a read-only token that expires."
          : "A clone is a new environment in the same tenant, restored from another one's backups. Deleted " +
            "environments whose backups were kept can be cloned too."}
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {clusters.length > 0 && (
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="From"
                value={cluster}
                disabled={submitting}
                onChange={(e) => {
                  setCluster(e.target.value);
                  setSourceQuery("");
                }}
              >
                <MenuItem value="">This cluster</MenuItem>
                {clusters.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} md={clusters.length > 0 ? 4 : 5}>
            <Autocomplete
              options={tenants}
              value={tenant}
              disabled={submitting}
              onChange={(_e, value) => setTenant(value)}
              onInputChange={(_e, value, reason) => reason === "input" && setTenantQuery(value)}
              getOptionLabel={(t) => t.tenant_name}
              isOptionEqualToValue={(a, b) => a.tenant_id === b.tenant_id}
              filterOptions={(x) => x}
              renderInput={(params) => <TextField {...params} label={cluster ? "Into tenant" : "Tenant"} />}
            />
          </Grid>
          <Grid item xs={12} md={clusters.length > 0 ? 6 : 7}>
            <Autocomplete<Source>
              options={(sources?.results ?? []) as Source[]}
              value={source}
              disabled={(!cluster && !tenant) || submitting}
              loading={sourcesPending}
              onChange={(_e, value) => setSource(value)}
              onInputChange={(_e, value, reason) => reason === "input" && setSourceQuery(value)}
              getOptionLabel={sourceLabel}
              isOptionEqualToValue={(a, b) => sourceKey(a) === sourceKey(b)}
              filterOptions={(x) => x}
              renderOption={(props, s) => (
                <li {...props} key={sourceKey(s)}>
                  <Box>
                    <Box sx={{ fontWeight: 500 }}>{sourceLabel(s)}</Box>
                    <Box sx={{ fontSize: 12, opacity: 0.7 }}>
                      {s.backup_sets} backup{s.backup_sets === 1 ? "" : "s"}
                      {s.newest_backup ? `, newest ${utcLabel(s.newest_backup)}` : ""}
                      {s.archived ? " · in Archive tier: rehydrate before cloning" : ""}
                    </Box>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={cluster ? `Source in ${cluster}` : "Source environment"}
                  helperText={
                    cluster
                      ? `${cluster}'s names cannot be read here: type its environment id.` +
                        (sources && sources.listed < sources.total
                          ? ` Showing the newest ${sources.listed} of ${sources.total}.`
                          : "")
                      : sources && sources.listed < sources.total
                      ? `Showing sources among ${sources.listed} of ${sources.total} environments; type a name to search.`
                      : " "
                  }
                />
              )}
            />
          </Grid>
        </Grid>
        {!!sources?.unreadable?.length && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Backups could not be read for: {sources.unreadable.map((u) => u.env_id ?? u.unreadable).join(", ")}. They
            are not offered.
          </Alert>
        )}
      </Paper>

      {windowPending && (
        <Box p={2} textAlign="center">
          <CircularProgress size={24} />
        </Box>
      )}

      {win && bounds && (
        <>
          <Typography variant="h6" gutterBottom>
            2. Point in time
          </Typography>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Typography variant="body2">
                Window: <strong>{utcLabel(win.earliest)}</strong> to <strong>{utcLabel(win.latest)}</strong>
              </Typography>
              {win.live && <Chip size="small" color="info" label="Running: the end moves while it writes" />}
              {win.deleted && <Chip size="small" label="Deleted" />}
              {win.cluster && <Chip size="small" color="info" label={`From ${win.cluster}'s backups: read only`} />}
            </Stack>
            {win.archived ? (
              <Alert severity="warning">
                <strong>This environment's backups are in Azure's Archive tier.</strong> Their names can be read but
                not their contents, so it cannot be cloned until every file under <code>{win.repository}</code> is
                rehydrated to Cool or Hot, which takes up to 15 hours.
                {win.rehydration ? ` Rehydration is in progress (${win.rehydration}).` : ""}
                <Box mt={1}>
                  Backup sets, by start time:{" "}
                  {win.backup_sets.map((b) => (b.stop ? utcLabel(b.stop) : b.label)).join(", ")}.
                </Box>
              </Alert>
            ) : (
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Restore to (UTC)"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 1, min: inputMin, max: inputMax }}
                      value={point ? moment.utc(point).format(INPUT_FORMAT) : ""}
                      onChange={(e) => {
                        setLatest(false);
                        setPoint(e.target.value ? moment.utc(e.target.value).toISOString() : "");
                      }}
                      error={!!problems.point && !!point}
                      helperText={problems.point && point ? problems.point : " "}
                    />
                    <Button
                      sx={{ mt: 1 }}
                      variant={latest ? "contained" : "outlined"}
                      onClick={() => {
                        setLatest(true);
                        setPoint(win.latest);
                      }}
                    >
                      Latest
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Jump to a backup"
                    value={!latest && groups.some((g) => g.entries.some((e) => e.value === point)) ? point : ""}
                    onChange={(e) => {
                      setLatest(false);
                      setPoint(e.target.value);
                    }}
                    SelectProps={{
                      renderValue: (v) => utcLabel(v as string),
                      MenuProps: { PaperProps: { sx: { maxHeight: 420 } } },
                    }}
                    helperText={`${win.backup_sets.length} backup${win.backup_sets.length === 1 ? "" : "s"}; any time in the window is valid`}
                  >
                    {groups.flatMap((g) => {
                      const full = g.entries.find((e) => e.label === g.full);
                      return [
                        <ListSubheader key={`full-${g.full}`}>
                          {full ? `Full backup, ${utcLabel(full.value)}` : `Full backup ${g.full}`}
                        </ListSubheader>,
                        ...g.entries.map((e) => (
                          <MenuItem key={e.value} value={e.value}>
                            <Box>
                              <Box>{`${utcLabel(e.value)} · ${e.kind}`}</Box>
                              <Box sx={{ fontSize: 12, opacity: 0.6 }}>{e.label}</Box>
                            </Box>
                          </MenuItem>
                        )),
                      ];
                    })}
                  </TextField>
                </Grid>
                {(latest || point) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {latest && win.cluster
                        ? `Latest: everything in ${win.cluster}'s WAL archive when the clone starts. Writes ${win.cluster} ` +
                          "has not archived by then are not in it."
                        : latest
                        ? "Latest: everything the source has written when the clone starts, its last write included."
                        : `${utcLabel(point)} is ${localLabel(point)} in your time zone. The clone holds what was committed by then.`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>

        </>
      )}

      {win && bounds && !win.archived && (
        <>
          <Typography variant="h6" gutterBottom>
            3. New environment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {win.source_spec_basis === "recorded"
              ? "Prefilled from the source's recorded spec."
              : win.source_spec_basis === "live"
              ? "Prefilled from the running source; it has no recorded spec."
              : win.cluster && floor
              ? `${win.cluster}'s spec cannot be read here: defaults, with storage sized for its ${floor} database.`
              : "The source's size is unknown: fill in the spec."}{" "}
            A clone is never the tenant's production environment.
          </Typography>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Name" value={form.name} onChange={set("name")}
                  error={!!problems.name} helperText={problems.name || " "} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="password" label="Password" value={form.password} onChange={set("password")}
                  helperText="Leave blank to generate one; it is kept in Key Vault" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Storage" value={form.storage} onChange={set("storage")}
                  error={!!problems.storage}
                  helperText={problems.storage || (floor ? `At least ${floor}` : " ")} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label="Disk type" value={form.storage_class} onChange={set("storage_class")}>
                  {storageClassOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label="Node pool" value={form.nodepool} onChange={set("nodepool")}>
                  {nodepoolOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label="Tier" value={form.tier} onChange={set("tier")}>
                  {environmentDBTiers.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth label="Resource allocation" value={form.resource_allocation}
                  onChange={set("resource_allocation")}>
                  {resourceAllocationOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth label="Minimum CPUs" value={form.cpu_req} onChange={set("cpu_req")}
                  error={!!problems.cpu_req} helperText={problems.cpu_req || " "} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth label="Minimum memory" value={form.memory_req} onChange={set("memory_req")}
                  error={!!problems.memory_req} helperText={problems.memory_req || " "} />
              </Grid>
              {form.resource_allocation !== "request_equals_limit" && (
                <>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth label="CPU limit" value={form.cpus ?? ""} onChange={set("cpus")}
                      helperText="Blank: no limit" />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth label="Memory limit" value={form.memory ?? ""} onChange={set("memory")}
                      helperText="Blank: no limit" />
                  </Grid>
                </>
              )}
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Backup container CPU" value={form.cpu_bkp} onChange={set("cpu_bkp")} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth label="Backup container memory" value={form.memory_bkp} onChange={set("memory_bkp")} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select fullWidth label="Backup storage account" value={form.backup_storage_account ?? ""}
                  onChange={set("backup_storage_account")} helperText="Where the clone's own backups go">
                  {accounts.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label="Full backups" value={form.full_backup_schedule}
                  onChange={set("full_backup_schedule")}>
                  {presets.full.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label="Incremental backups" value={form.incremental_backup_schedule}
                  onChange={set("incremental_backup_schedule")}>
                  {presets.incremental.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={!!form.rule_engine} onChange={set("rule_engine")} />}
                  label="Rule engine (created empty; the source's is not copied)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={form.expose_externally} onChange={set("expose_externally")} />}
                  label="Expose externally"
                />
              </Grid>
              {form.expose_externally && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Exposure name"
                    value={form.exposure_name}
                    onChange={set("exposure_name")}
                    placeholder={exposureNameFor({ ...form, exposure_name: "" }, tenant?.tenant_name)}
                    error={!!problems.exposure_name}
                    helperText={problems.exposure_name || "Blank: derived from the tenant and the name"}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" disabled={!ready || submitting} onClick={() => setConfirm(true)}>
              {submitting ? "Cloning…" : "Clone"}
            </Button>
          </Stack>
        </>
      )}

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>Clone {source ? sourceLabel(source) : ""}?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            A new environment <strong>{form.name.trim()}</strong> in {tenant?.tenant_name} is created and restored
            from the source's backups as of{" "}
            <strong>{latest ? "the moment the clone starts (everything written by then)" : point ? utcLabel(point) : ""}</strong>,
            with {form.storage}{" "}
            of storage.{" "}
            {win?.cluster
              ? `${win.cluster}'s backups are only read, with a read-only token; nothing in ${win.cluster} is changed.`
              : `The source is only read${win?.live ? ", after its current WAL is archived" : ""}.`}{" "}
            {form.expose_externally
              ? `It is exposed externally as ${exposureNameFor(form, tenant?.tenant_name)}.`
              : "It is not exposed externally."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => submit()} disabled={submitting}>
            Clone
          </Button>
        </DialogActions>
      </Dialog>

      {/* The create form's dialog: only an unavailable port offers going on unexposed (review). */}
      <ExposureWarningDialog
        open={noPort !== null}
        preflight={noPort}
        gateway={noPort?.gateway || undefined}
        onProceed={() => {
          setNoPort(null);
          submit(true);
        }}
        onCancel={() => setNoPort(null)}
      />
    </Box>
  );
};

export default EnvironmentClone;
